import { useMemo, useState } from "react";
import { Archive, ChevronRight, Pencil, Plus, Trash2, Users } from "lucide-react";
import type { DashboardData, IOU, Payee, Transaction } from "../../types";
import { dateLabel, isEarnable, isSpendable, money, shortName } from "../../lib/finance";
import { categoryLabel } from "../../lib/allocations";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import FormActions from "../../components/FormActions";
import Modal from "../../components/Modal";
import PayeeAvatar from "../../components/PayeeAvatar";
import { api } from "../../lib/api";
import { payeeEntryCounts, payeeNeed, type Need } from "../../lib/coverage";
import { useLedgerNeed } from "../../app/use-ledger-need";
import SecondaryHeading from "../../app/SecondaryHeading";
import PayeeDirectory from "./PayeeDirectory";

// `type` is no longer asked for or shown: nothing in the app ever branched on Person vs Business,
// and the inline add-a-payee path filled it in as Person regardless, so a shop was mislabelled the
// moment it was created that way. It stays on the draft only so an edit writes back what the
// workbook already holds — Sheets is still the source of truth, and this is not the change that
// should start rewriting a column there. The field goes for good at cutover.
type Draft = { id?: string; name: string; type: "Person" | "Business"; notes: string };
const blank = (): Draft => ({ name: "", type: "Person", notes: "" });

export default function Payees({ data, ensure, onSaved, onOpenTransaction, onOpenIou }: { data: DashboardData; ensure: (need: Need) => void; onSaved: () => Promise<unknown>; onOpenTransaction: (t: Transaction) => void; onOpenIou: (id: string) => void }) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Counted once per data load: the list asks each payee up to seven times per render.
  const payees = data.payees || [];
  const ious = useMemo(() => data.ious || [], [data.ious]);
  const transactions = useMemo(() => data.transactions || [], [data.transactions]);
  const accounts = data.accounts || [];
  const usage = useMemo(() => {
    const byPayee = payeeEntryCounts({ coverage: data.coverage, transactions }), openLoans = new Map<string, number>(), loans = new Set<string>();
    for (const i of ious) {
      loans.add(i.payeeId);
      if (i.status === "Open") openLoans.set(i.payeeId, (openLoans.get(i.payeeId) || 0) + 1);
    }
    return { transactions: byPayee, openLoans, loans };
  }, [data.coverage, transactions, ious]);
  const used = (id: string) => usage.transactions.has(id) || usage.loans.has(id);
  const detail = payees.find((p) => p.id === detailId) || null;
  // The list is counted from the digest; a payee's own entries are loaded back to its oldest when opened.
  const detailReady = useLedgerNeed(data, detail ? payeeNeed(data, detail.id) : null, ensure);
  const payeeTransactions = detail
    ? transactions.filter((t) => t.payeeId === detail.id).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
    : [];
  const payeeIous = detail
    ? ious.filter((i) => i.payeeId === detail.id).sort((a, b) => String(b.createdDate || "").localeCompare(String(a.createdDate || "")))
    : [];
  // Loan cash is left out of these two: it is not spending or income, and it is already accounted
  // for by the loans listed above them. Counting it here would say it twice.
  const spent = payeeTransactions.filter(isSpendable).reduce((sum, t) => sum + t.amount, 0);
  const received = payeeTransactions.filter(isEarnable).reduce((sum, t) => sum + t.amount, 0);
  // One fact, never two: the row is ~180px wide on a phone and both counts together wrap to a
  // second line. An outstanding loan outranks a transaction count, and the detail has the rest.
  // No leading separator: this is the whole secondary line now that the type label has gone.
  const countLabel = (id: string) => {
    const open = usage.openLoans.get(id) || 0;
    if (open) return `${open} open IOU${open === 1 ? "" : "s"}`;
    const txns = usage.transactions.get(id) || 0;
    return txns ? `${txns} transaction${txns === 1 ? "" : "s"}` : "";
  };

  function edit(payee: Payee) {
    setDraft({ id: payee.id, name: payee.name, type: payee.type, notes: payee.notes });
    setError("");
  }
  function askRemove(payee: Payee) {
    const isUsed = used(payee.id);
    setConfirm({
      title: isUsed ? "Archive this payee?" : "Delete this unused payee?",
      message: isUsed ? "It will disappear from new IOUs and transactions. Existing history remains available." : "This payee has no transactions or IOUs and will be permanently removed.",
      confirmLabel: isUsed ? "Archive payee" : "Delete payee",
      danger: !isUsed,
      onConfirm: () => {
        if (detailId === payee.id) setDetailId(null);
        return remove(payee.id);
      },
    });
  }

  async function save() {
    if (!draft || busy) return;
    setBusy(true); setError("");
    try {
      await api("payees/save", draft);
      setDraft(null);
      await onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    setBusy(true); setError("");
    try {
      await api("payees/remove", { id });
      await onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <section className="panel account-settings">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <SecondaryHeading title="Payees" icon={<Users size={18} />}>
        <button className="primary small" onClick={() => { setDraft(blank()); setError(""); }}><Plus size={14} /> Add</button>
      </SecondaryHeading>
      {error && !draft && <div className="inline-error" role="alert">{error}</div>}
      {draft && (
        <Modal
          title={draft.id ? "Edit payee" : "New payee"}
          onClose={() => {
            setDraft(null);
            setError("");
          }}
        >
          <form
            className="account-editor"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            {error && <div className="inline-error" role="alert">{error}</div>}
            <label>
              Name
              <input
                required
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Ali"
              />
            </label>
            <label>
              <span className="field-label">Notes <small>Optional</small></span>
              <textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </label>
            <FormActions
              label="Save payee"
              busy={busy}
              disabled={!draft.name.trim()}
            />
          </form>
        </Modal>
      )}
      <PayeeDirectory
        payees={payees}
        busy={busy}
        countLabel={countLabel}
        used={used}
        onOpen={setDetailId}
        onEdit={edit}
        onRemove={askRemove}
      />
      {detail && (
        <Modal
          title={detail.name}
          className="payee-detail"
          onClose={() => setDetailId(null)}
          headerActions={
            <>
              <button
                className="icon-button"
                onClick={() => edit(detail)}
                aria-label={`Edit ${detail.name}`}
              >
                <Pencil size={18} />
              </button>
              <button
                className={`icon-button ${used(detail.id) ? "" : "danger-icon-button"}`}
                disabled={busy}
                onClick={() => askRemove(detail)}
                aria-label={`${used(detail.id) ? "Archive" : "Delete"} ${detail.name}`}
              >
                {used(detail.id) ? <Archive size={18} /> : <Trash2 size={18} />}
              </button>
            </>
          }
        >
          <div className="detail-hero">
            <PayeeAvatar id={detail.id} name={detail.name} size={56} />
            <h3>{detail.name}</h3>
            {detail.notes && <p>{detail.notes}</p>}
          </div>
          <dl className="detail-list">
            <div><dt>Transactions</dt><dd>{detailReady ? payeeTransactions.length : usage.transactions.get(detail.id) || 0}</dd></div>
            {detailReady && spent > 0 && <div><dt>Spent</dt><dd>{money(spent, data.baseCurrency)}</dd></div>}
            {detailReady && received > 0 && <div><dt>Received</dt><dd>{money(received, data.baseCurrency)}</dd></div>}
          </dl>
          {payeeIous.length > 0 && (
            <div className="payee-section">
              <h4>Loans</h4>
              <div className="linked-txn-list">
                {payeeIous.map((iou: IOU) => (
                  <div className="linked-txn-row" key={iou.id}>
                    <button type="button" className="linked-txn-main" onClick={() => onOpenIou(iou.id)}>
                      <span>
                        <strong>{iou.description}</strong>
                        <small>{iou.direction === "Owed to us" ? "Owed to you" : "You owe"} · {iou.status}</small>
                      </span>
                      <span className={iou.direction === "Owed to us" ? "income" : ""}>
                        {money(iou.status === "Open" ? iou.outstanding : iou.originalAmount, iou.currency)}
                        <ChevronRight size={15} />
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="payee-section">
            <h4>Transactions</h4>
            {!detailReady ? (
              <LoadingSkeleton layout="detail" label="Loading payee transactions" />
            ) : payeeTransactions.length ? (
              <div className="linked-txn-list">
                {payeeTransactions.map((t) => {
                  const income = t.type.toLowerCase() === "income";
                  return (
                    <div className="linked-txn-row" key={t.id}>
                      <button type="button" className="linked-txn-main" onClick={() => onOpenTransaction(t)}>
                        <span>
                          <strong>{categoryLabel(t) || (income ? "Income" : "Expense")}</strong>
                          <small>{dateLabel(t.date)} · {shortName(accounts.find((a) => a.id === t.accountId)?.name || t.accountId)}</small>
                        </span>
                        <span className={income ? "income" : ""}>
                          {income ? "+" : "\u2212"}{money(t.amount, t.currency)}
                          <ChevronRight size={15} />
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="muted empty-small">Nothing recorded against this payee yet.</p>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
